/* **What a task card shows under its heading**, per kind — KANON "Karty zadań", 24 September 2026
 * (`docs/design/karty-zadan-2026-09-24/`, `catalogue6` for the `/zadania` card, `canvas`/`zk` for the
 * Talia card on the Przegląd and the Pulpit). One stylesheet for the blocks because they are one
 * vocabulary — a quote, an exercise line, a row of acts, a month — and three densities of it:
 *
 *   desk   the `/zadania` card, 836 wide, its body scrolling inside 730
 *   quick  the Przegląd's Talia card, the right-hand column of a 286-tall card
 *   short  the Pulpit's "Szybki feedback" card, narrower and shorter still
 *
 * Every value is a token; the few layout heights are layout and say so.
 *
 * Text that must be read is `--text-2` where the canon writes `--text-3`: `--text-3` measures 3.0:1
 * (Flame) and 4.0:1 (Flame Dark) on these surfaces — the standing deviation `DetailTab.module.css`
 * records, kept by the 31.08 decision on a measured contrast number. */

/* ── The stack every block stands in ─────────────────────────────────────────────────────────── */

.stack {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  min-width: 0;
}

.quick {
  gap: var(--space-2);
}

/* The Pulpit's card: the words stand right under the chips. */
.short {
  gap: var(--space-6px);
}

/* ── The client's own words ─────────────────────────────────────────────────────────────────── */

.quote {
  margin: var(--space-0);
  padding: var(--space-10px) var(--space-3);
  font-size: var(--fs-note);
  font-weight: var(--fw-medium);
  line-height: var(--lh-note);
  color: var(--text);
  background: var(--bg-inset);
  border-radius: var(--radius-12px);
}

.quoteLabel {
  display: block;
  margin-block-end: var(--space-2px);
  font-size: var(--fs-eyebrow);
  font-weight: var(--fw-bold);
  color: var(--text-2);
}

/* The quick and short cards' words, clamped to two lines and holding both from the first frame,
 * so the slot's height does not depend on the answer. */
.said {
  display: -webkit-box;

  /* Rounded up to a whole pixel: 2 × 12px × 1.4 is 33.6, and a fractional box put everything under
   * it on a sub-pixel offset that a scroll into view cannot land flush (the Pulpit's composer). */
  height: round(up, calc(2em * var(--lh-hint)), 1px);
  overflow: hidden;
  font-size: var(--fs-label);
  font-weight: var(--fw-medium);
  line-height: var(--lh-hint);
  color: var(--text-2);
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

/* The two small cards say the words in ONE line (owner, 25 September 2026: the second line's room
 * stood empty above the foot for most sessions); the whole of them is one press away, in the session. */
.quick .said,
.short .said {
  height: round(up, calc(1em * var(--lh-hint)), 1px);
  -webkit-line-clamp: 1;
}

/* The wait: a block box, so the bar inside can take the first line's height. */
.saidWait {
  display: block;
}

.saidBar {
  display: block;
  width: 80%;
  height: 1em;
}

.saidNone {
  font-style: italic;
}

/* ── The session's key readout: the ring and what stood out ──────────────────────────────────── */

.key {
  display: flex;
  gap: var(--space-22px);
  align-items: center;
}

/* The Pulpit's card: the ring and the column beside it, centred on each other (owner, 26 September
 * 2026). Every part of the column holds its height from the first frame — see `.chipBand` — so
 * centring moves nothing when the read lands. */
.short .key {
  gap: var(--space-4);
}

/* The session's name and length, opening the column — the head's meta line, moved here. */
.glanceLine {
  height: round(up, calc(1em * var(--lh-hint)), 1px);
  margin: var(--space-0);
  overflow: hidden;
  font-size: var(--fs-label);
  font-weight: var(--fw-medium);
  line-height: var(--lh-hint);
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--text-2);
}

/* The chips' band: two chip lines from the first frame, the loaded chips centred in it. Five chips
 * and the night's sleep wrap to two lines at the card's width; the wait draws one hole. A band that
 * grew on arrival pushed every card under it, and one packed to the top left an empty line over the
 * words for a session with little to say. */
.chipBand {
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: calc(2 * var(--space-6) + var(--space-6px));
}

/* The desk's column beside the ring: the chips, and the client's words under them. */
.keySide {
  display: flex;
  flex: 1;
  flex-direction: column;

  /* Packed to the top, so the chips stand where they stood while the words were still out. */
  align-self: flex-start;
  gap: var(--space-3);
  min-width: 0;
}

.short .keySide {
  align-self: center;
  gap: var(--space-6px);
}

/* The desk's session: its key, its lines and its open note field, a step closer to each other so the
 * three are read together without scrolling at 1512×862 (owner, 25 September 2026). It takes the
 * body's whole height, loaded and waiting alike, so the note can stand at its foot. */
.desk.session {
  flex: 1 1 auto;
  gap: var(--space-6px);
  min-height: 0;
}

/* The note stands at the foot of the body, the room the detail leaves above it (owner, 25 September
 * 2026). Shrinkable, so a short window takes the field's third line back before the body scrolls. */
.noteSlot {
  display: flex;
  flex-direction: column;
  min-height: 0;
  margin-block-start: auto;
}

/* The ring and the words under it read as one object. */
.ring {
  display: flex;
  flex: none;
  flex-direction: column;
  gap: var(--space-1);
  align-items: center;
}

.ringCaption {
  font-size: var(--fs-caption);
  font-weight: var(--fw-semibold);
  white-space: nowrap;
  color: var(--text-2);
}

/* ── Exercise lines: one mark per set ───────────────────────────────────────────────────────── */

.lines {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
}

.line {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 120px 96px;
  gap: var(--space-10px);
  align-items: center;
  min-height: 31px;
  border-bottom: var(--border-hairline) solid var(--border);
}

/* The desk's lines are compact (owner, 25 September 2026): the session's lines and its open note
 * field are read together without scrolling at 1512×862. */
.desk .line {
  min-height: 28px;
}

/* The Przegląd's three lines, a notch tighter still: the card holds one height (258). */
.linesQuick .line {
  min-height: 26px;
}

.line:last-child {
  border-bottom: none;
}

.lineName {
  overflow: hidden;
  font-size: var(--fs-note);
  font-weight: var(--fw-bold);
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--text);
}

.lineFigure {
  font-size: var(--fs-label);
  font-weight: var(--fw-bold);
  font-variant-numeric: tabular-nums;
  text-align: end;
  white-space: nowrap;
  color: var(--text-2);
}

.lineDown {
  color: var(--danger-ink);
}

.lineRecord {
  color: var(--lime-ink);
}

/* The trophy before a record line's figure, on the personal-record card (`ExerciseLines`). */
.lineMark {
  width: 12px;
  height: 12px;
  margin-inline-end: var(--space-1);
  vertical-align: var(--space-minus-2px);
}

.lineRest {
  padding-block: var(--space-6px);
  font-size: var(--fs-micro);
  font-weight: var(--fw-semibold);
  color: var(--text-2);
}

.desk .lineRest {
  padding-block: var(--space-1) var(--space-0);
}

.sets {
  display: inline-flex;
  flex-wrap: wrap;
  gap: var(--space-2px);
}

.set {
  width: 8px;
  height: 8px;
  background: var(--text-2);
  border-radius: var(--radius-hair);
}

.setOpen,
.setSkipped {
  --set-edge: inset 0 0 0 var(--border-hairline) var(--text-3);

  background: transparent;
  box-shadow: var(--set-edge);
}

.setDown {
  background: var(--danger);
}

.setUp {
  background: var(--good);
}

.setRecord {
  background: var(--pr);
}

/* ── A row of acts: a label, the choices, the primary at its end ─────────────────────────────── */

.acts {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-6px);
  align-items: center;
  padding: var(--space-6px) var(--space-10px);
  background: var(--bg-inset);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-12px);
}

.actsLabel {
  margin-inline-end: var(--space-1);
  font-size: var(--fs-label);
  font-weight: var(--fw-bold);
  color: var(--text-2);
}

/* The Talia card's row stands bare: its label would repeat the head, and a boxed row was one more
 * frame on a card read at a glance (owner, 25 September 2026). */
.quick .acts {
  padding: var(--space-0);
  background: none;
  border-style: none;
}

.actsSpacer {
  flex: 1;
}

.pick {
  display: inline-flex;
  align-items: center;
  height: 28px;
  padding: var(--space-0) var(--space-10px);
  font-family: var(--font-body);
  font-size: var(--fs-micro);
  font-weight: var(--fw-bold);
  white-space: nowrap;
  color: var(--text-2);
  cursor: pointer;
  background: var(--bg-chip);
  border-width: var(--border-hairline);
  border-style: solid;
  border-color: transparent;
  border-radius: var(--r-pill);
}

.pickOn {
  color: var(--text);
  background: var(--bg-card);
  border-color: var(--text-2);
}

/* `inkButton` / `ghostButton` are treatments; the geometry is the surface's. */
.actGhost {
  background: transparent;
}

.act {
  gap: var(--space-6px);
  height: 30px;
  padding: var(--space-0) var(--space-3);
  font-size: var(--fs-label);
  font-weight: var(--fw-bold);
  white-space: nowrap;
  border-radius: var(--radius-sm);
}

/* The glyph beside an act's word: 16px, in the control's own ink — white on the one ink-filled
 * primary, neutral on every ghost. Never the accent (the accent is a scalpel). The desk's alone: the
 * Talia card's act row is narrower and holds one height, and its canon draws the words bare. */
.actGlyph {
  flex: none;
  width: 16px;
  height: 16px;
}

.quick .actGlyph,
.short .actGlyph {
  display: none;
}

.refused {
  flex-basis: 100%;
  font-size: var(--fs-label);
  color: var(--danger-ink);
}

.doneMark {
  font-size: var(--fs-label);
  font-weight: var(--fw-bold);
  color: var(--good-ink);
}

/* ── The chat's honest line: the conversation is in the app ─────────────────────────────────── */

.chat {
  display: inline-flex;
  gap: var(--space-6px);
  align-items: center;
  align-self: flex-start;
  font-size: var(--fs-label);
  font-weight: var(--fw-bold);
  color: var(--text-2);
  text-decoration: underline;
  text-underline-offset: var(--space-3px);
}

.note {
  font-size: var(--fs-micro);
  line-height: var(--lh-note);
  color: var(--text-2);
}

.sectionTitle {
  font-size: var(--fs-label);
  font-weight: var(--fw-bold);
  color: var(--text-2);
}

/* ── Tiles: a small label over a figure ────────────────────────────────────────────────────────
 * The four weeks of attendance and a missed workout's „Sesja" (owner, 26 September 2026: back from
 * the first desk card, `zadania-c-runda5.html`'s `.facts` / `.weeks`). One treatment for both. */

.tiles {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: var(--space-10px);
}

.tile {
  min-width: 0;
  padding: var(--space-10px) var(--space-3);
  background: var(--bg-inset);
  border-radius: var(--radius-13px);
}

.quick .tile {
  padding: var(--space-6px) var(--space-10px);
}

.tileLabel {
  overflow: hidden;
  font-size: var(--fs-tag);
  font-weight: var(--fw-bold);
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--text-2);
}

.tileValue {
  margin-top: var(--space-1px);
  overflow: hidden;
  font-size: var(--fs-label-md);
  font-weight: var(--fw-extrabold);
  font-variant-numeric: tabular-nums;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--text);
}

/* ── The month ──────────────────────────────────────────────────────────────────────────────── */

.month {
  display: flex;
  flex-direction: column;
  gap: var(--space-5px);
}

.monthRow {
  display: grid;
  grid-template-columns: 54px repeat(7, minmax(0, 1fr)) 50px;
  gap: var(--space-5px);
  align-items: stretch;
}

/* The Przegląd's card holds one height: four weeks, their head and the legend stand in its body. */
.quick .month {
  gap: var(--space-3px);
}

.quick .monthRow,
.short .monthRow {
  grid-template-columns: 44px repeat(7, minmax(0, 1fr)) 40px;
  gap: var(--space-3px);
}

.monthHead {
  font-size: var(--fs-caption);
  font-weight: var(--fw-bold);
  text-align: center;
  color: var(--text-2);
}

.monthLabel {
  align-self: center;
  font-size: var(--fs-caption);
  font-weight: var(--fw-bold);
  white-space: nowrap;
  color: var(--text-2);
}

.monthTally {
  align-self: center;
  font-size: var(--fs-note);
  font-weight: var(--fw-extrabold);
  font-variant-numeric: tabular-nums;
  text-align: center;
  color: var(--text);
}

.monthTallyShort {
  color: var(--danger-ink);
}

.monthTallyAhead {
  color: var(--text-2);
}

/* The day and its word share the first line, the hint takes the second: two lines where there were
 * three, so the desk's month stands 48px a row (owner, 25 September 2026: read without scrolling). */
.cell {
  display: flex;
  flex-flow: row wrap;
  gap: var(--space-1px) var(--space-1);
  align-content: flex-start;
  min-width: 0;
  height: 44px;
  padding: var(--space-5px) var(--space-7px);
  overflow: hidden;
  font-size: var(--fs-caption);
  line-height: var(--lh-normal);
  color: var(--text-2);
  background: var(--bg-chip);
  border-radius: var(--radius-mark);
}

.quick .cell,
.short .cell {
  height: 20px;
  padding: var(--space-2px) var(--space-5px);
  flex-flow: row nowrap;
  gap: var(--space-1);
  align-items: center;
  justify-content: center;
}

.cellDay {
  font-weight: var(--fw-bold);
  font-variant-numeric: tabular-nums;
}

/* Beside the day where it fits; a word too long for that („opuszczona") takes the second line whole
 * rather than losing its end. */
.cellTitle {
  max-width: 100%;
  overflow: hidden;
  font-weight: var(--fw-bold);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cellHint {
  flex-basis: 100%;
  font-size: var(--fs-badge);
  color: var(--text-2);
}

.cellDone {
  color: var(--bg-card);
  background: var(--text);
}

.cellDone .cellTitle {
  color: var(--bg-card);
}

.cellMissed,
.cellAhead,
.cellGhost,
.cellToday {
  --cell-edge: inset 0 0 0 var(--border-hairline) var(--border);

  background: transparent;
  box-shadow: var(--cell-edge);
}

.cellMissed {
  --cell-edge: inset 0 0 0 var(--border-ring) var(--danger);
}

.cellMissed .cellTitle {
  color: var(--danger-ink);
}

.cellGhost {
  --cell-edge: inset 0 0 0 var(--border-hairline) var(--border-2);
  --cell-hatch: repeating-linear-gradient(135deg, transparent 0 6px, var(--bg-chip) 6px 7px);

  background-image: var(--cell-hatch);
}

.cellGhost .cellTitle {
  color: var(--text-2);
}

.cellToday {
  --cell-edge: inset 0 0 0 var(--border-ring) var(--text);
}

.cellToday .cellTitle,
.cellToday .cellDay {
  color: var(--text);
}

/* The last day the client trained, on a lapse: the one accent ring on the card — it IS the fact.
 * Inset, over the square's own fill, so the trained square stays trained (canon `.cv-mc-c.last`). */
.cellLast {
  --cell-edge: inset 0 0 0 var(--border-ring) var(--accent);

  box-shadow: var(--cell-edge);
}

.legend {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-6px) var(--space-4);
  align-items: center;
  margin-block-start: var(--space-1);
  font-size: var(--fs-caption);
  color: var(--text-2);
}

.legendItem {
  display: inline-flex;
  gap: var(--space-5px);
  align-items: center;
}

.legendSwatch {
  width: 10px;
  height: 10px;
  background: var(--bg-chip);
  border-radius: var(--radius-hair);
}

.legendDone {
  background: var(--text);
}

.legendMissed {
  --swatch-edge: inset 0 0 0 var(--border-ring) var(--danger);

  background: transparent;
  box-shadow: var(--swatch-edge);
}

.legendGhost {
  --swatch-edge: inset 0 0 0 var(--border-hairline) var(--border-2);

  background: transparent;
  box-shadow: var(--swatch-edge);
}

.legendNote {
  margin-inline-start: auto;
}

/* ── The plan: its cover, the weeks, the balance ────────────────────────────────────────────── */

.cover {
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;

  /* Owner, 25 September 2026: 150 was a poster too tall for a card read whole without scrolling,
   * and the 96px band that replaced it squashed the photograph flat. 140, with the room taken back
   * from the weeks and the balance under it. */
  height: 140px;
  padding: var(--space-4) var(--space-5);
  overflow: hidden;
  color: var(--text-on-photo);
  background: var(--bg-sunken);
  border-radius: var(--radius-md);
}

/* The Przegląd's cover (owner, 25 September 2026): the left half of the block, tall enough for the
 * photograph to read — the 56px band it replaced flattened it to a stripe — with the category and
 * the name inside, a size smaller than the desk's. */
.quick .cover {
  height: var(--h-talia-plan-cover);
  padding: var(--space-3) var(--space-14px);
}

/* The cover and, beside it, the weeks and the three rows of the balance. */
.planQuick {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: var(--space-4);
  align-items: center;
}

.planSide {
  justify-content: center;
}

.planRows {
  display: grid;
  margin: var(--space-0);
}

.planRow {
  display: flex;
  justify-content: space-between;
  gap: var(--space-2);
  font-size: var(--fs-label);
  line-height: var(--lh-hint);
  color: var(--text-2);
}

.planRow dd {
  margin: var(--space-0);
  font-weight: var(--fw-bold);
  font-variant-numeric: tabular-nums;
  color: var(--text);
}

.planRowBad dd {
  color: var(--danger);
}

.coverPhoto {
  position: absolute;
  inset: var(--space-0);
  width: 100%;
  height: 100%;
}

/* The shade rises from the BOTTOM (owner, 23 September 2026), under the name. */
.coverShade {
  position: absolute;
  inset: var(--space-0);
  background: var(--scrim-cover);
}

.coverText {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

.coverCategory {
  font-size: var(--fs-eyebrow);
  font-weight: var(--fw-bold);
  letter-spacing: var(--ls-rail);
  text-transform: uppercase;
}

.coverName {
  overflow: hidden;
  font-size: var(--fs-stat);
  font-weight: var(--fw-extrabold);
  letter-spacing: var(--ls-title);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.quick .coverName {
  font-size: var(--fs-body);
}

.coverFigure {
  position: absolute;
  inset-block-start: var(--space-3);
  inset-inline-end: var(--space-4);
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  font-size: var(--fs-empty);
  font-weight: var(--fw-extrabold);
  line-height: var(--lh-none);
  letter-spacing: var(--ls-home-figure);
  font-variant-numeric: tabular-nums;
}

.coverUnit {
  font-size: var(--fs-label-md);
  letter-spacing: normal;
}

.coverFigure i {
  margin-block-start: var(--space-1);
  font-size: var(--fs-caption);
  font-style: normal;
  font-weight: var(--fw-semibold);
  letter-spacing: normal;
}

/* The bar and its axis read as one strip. */
.weeks {
  gap: var(--space-1);
}

.weeksBar {
  display: grid;
  grid-template-columns: repeat(var(--weeks), minmax(0, 1fr));
  gap: var(--space-3px);
  align-items: center;
}

.weekSeg {
  height: 5px;
  background: var(--bg-chip);
  border-radius: var(--radius-bar);
}

.weekDone {
  background: var(--accent-grad-rule);
}

.weekNow {
  height: 9px;
  background: var(--accent-grad-rule);
}

.weeksAxis {
  display: flex;
  justify-content: space-between;
  font-size: var(--fs-eyebrow);
  font-weight: var(--fw-semibold);
  color: var(--text-2);
}

/* The Talia card has one fixed height; its week bar speaks for itself without the axis under it. */
.quick .weeksAxis {
  display: none;
}

/* A step tighter than the canon's 12/10 (owner, 25 September 2026): the room the cover took back. */
.stats {
  display: flex;
  flex-direction: column;
  gap: var(--space-6px);
  padding: var(--space-2) var(--space-14px);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-md);
}

.statFigures {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: var(--space-2);
}

.stat {
  display: flex;
  flex-direction: column;
  font-size: var(--fs-micro);
  color: var(--text-2);
}

.stat b {
  font-size: var(--fs-stat);
  font-weight: var(--fw-extrabold);
  line-height: var(--lh-plan-hero);
  font-variant-numeric: tabular-nums;
  color: var(--text);
}

.statSmall {
  font-size: var(--fs-label);
  font-weight: var(--fw-semibold);
  color: var(--text-2);
}

.statBad b {
  color: var(--danger-ink);
}

.strip {
  display: flex;
  gap: var(--space-6px);
  align-items: flex-end;
}

.stripWeek {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: var(--space-2px);
  min-width: 0;
}

.stripMark {
  height: 6px;
  background: var(--text);
  border-radius: var(--radius-hair);
}

.stripMissed {
  background: var(--danger);
}

.stripAhead,
.stripOther {
  --strip-edge: inset 0 0 0 var(--border-hairline) var(--border-2);

  background: transparent;
  box-shadow: var(--strip-edge);
}

.stripNumber {
  margin-block-start: var(--space-2px);
  font-size: var(--fs-badge);
  text-align: center;
  color: var(--text-3);
}

.stripLeft {
  align-self: center;
  margin-inline-start: var(--space-6px);
  font-size: var(--fs-micro);
  white-space: nowrap;
  color: var(--text-2);
}

/* ── The goal ───────────────────────────────────────────────────────────────────────────────── */

.goalHead {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: var(--space-3);
  font-size: var(--fs-label-md);
}

.goalTitle {
  font-weight: var(--fw-bold);
  color: var(--text);
}

/* Three lines at most: a long description must not push the acts out of the card. */
.goalDescription {
  display: -webkit-box;
  overflow: hidden;
  font-size: var(--fs-label);
  line-height: var(--lh-hint);
  color: var(--text-2);
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
}

.goalBar {
  position: relative;
  height: 6px;
  background: var(--bg-chip);
  border-radius: var(--radius-bar);
}

.goalFill {
  position: absolute;
  inset: var(--space-0) auto var(--space-0) var(--space-0);
  width: calc(var(--goal) * 100%);
  background: var(--accent-grad-rule);
  border-radius: var(--radius-bar);
}

.goalMarker {
  position: absolute;
  inset-block-start: calc(var(--space-3px) * -1);
  inset-inline-start: calc(var(--goal) * 100%);
  width: 3px;
  height: 12px;
  background: var(--accent);
  border-radius: var(--radius-hair);
  transform: translateX(-50%);
}

.goalAxis {
  display: flex;
  justify-content: space-between;
  font-size: var(--fs-caption);
  color: var(--text-2);
}

.goalNow {
  font-weight: var(--fw-bold);
  color: var(--text);
}

/* The goal's span, set → deadline, on the bar's own track (owner, 26 September 2026). Time is not a
 * verdict, so its fill is neutral ink: the accent stays the progress bar's. */
.timeline .goalFill {
  background: var(--text-3);
}

.timeline .goalMarker {
  background: var(--text);
}

/* ── The invitation ─────────────────────────────────────────────────────────────────────────── */

.invitation {
  display: flex;
  gap: var(--space-5);
  align-items: center;
  padding: var(--space-14px) var(--space-4);
  background: var(--bg-inset);
  border-radius: var(--radius-md);
}

.envelope {
  position: relative;
  flex: none;
  width: 132px;
  height: 96px;
  color: var(--text-3);
}

.envelopeSeal {
  fill: var(--task-ink-sand);
  stroke: var(--bg-card);
  stroke-width: 3;
}

.envelopeFigure {
  position: absolute;
  inset-block-start: var(--space-1);
  inset-inline-end: var(--space-3px);
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 34px;
  font-size: var(--fs-sheet-title);
  font-weight: var(--fw-extrabold);
  line-height: var(--lh-none);
  color: var(--bg);
}

.envelopeUnit {
  font-size: var(--fs-badge);
  font-weight: var(--fw-bold);
}

.invitationText {
  display: flex;
  flex-direction: column;
  gap: var(--space-6px);
  min-width: 0;
}

.invitationHead {
  font-size: var(--fs-h3);
  font-weight: var(--fw-extrabold);
  letter-spacing: var(--ls-title);
  color: var(--text);
}

.invitationMeta {
  font-size: var(--fs-label);
  color: var(--text-2);
}

.invitationMeta b {
  font-weight: var(--fw-bold);
  color: var(--text);
}

.message {
  display: flex;
  flex-direction: column;
  gap: var(--space-6px);
  padding: var(--space-10px);
  background: var(--bg-inset);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-12px);
}

.messageField {
  width: 100%;
  min-height: 44px;
  padding: var(--space-0);
  font-family: var(--font-body);
  font-size: var(--fs-note);
  line-height: var(--lh-note);
  color: var(--text);
  resize: none;
  background: transparent;
  border: none;
}

.messageField:focus-visible {
  outline: var(--border-focus) solid var(--accent);
  outline-offset: var(--space-2px);
}

.messageFoot {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
  align-items: center;
  justify-content: flex-end;
}

/* ── The client's message ───────────────────────────────────────────────────────────────────── */

.bubble {
  align-self: flex-start;
  max-width: 75%;
  padding: var(--space-10px) var(--space-14px);
  font-size: var(--fs-body);
  line-height: var(--lh-note);
  color: var(--text);
  background: var(--bg-chip);
  border-radius: var(--radius-16px) var(--radius-16px) var(--radius-16px) var(--radius-4px);
}

/* ── The clip, with what explains it beside it ──────────────────────────────────────────────── */

.clip {
  display: flex;
  gap: var(--space-4);
  align-items: flex-start;
  min-width: 0;
}

.desk.clipStack {
  flex-direction: column;
  align-items: center;
}

.clipSide {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: var(--space-2);
  align-self: stretch;
  justify-content: center;
  min-width: 180px;
}

.question {
  font-size: var(--fs-body);
  font-weight: var(--fw-medium);
  line-height: var(--lh-relaxed);
  color: var(--text);
}

/* The Przegląd's card: the poster as tall as the body — the card's one height, not a fixed box
 * the words beside it have to scroll past — and the question beside it, clamped to what that height
 * holds. The whole of it is one press away: the foot's primary opens the player, which carries the
 * discussion. */
.clip.quick {
  height: 100%;
}

.quick .question {
  display: -webkit-box;
  overflow: hidden;
  -webkit-line-clamp: 5;
  -webkit-box-orient: vertical;
}

/* The Pulpit's clip row (owner, 26 September 2026): the small poster on the left
 * (`clipFrame.module.css` `.short`), the meta line and the question beside it, centred on it. */
.clip.short {
  align-items: center;
}

.short .clipSide {
  gap: var(--space-6px);
  min-width: 0;
}

.short .question {
  display: -webkit-box;
  overflow: hidden;
  font-size: var(--fs-note);
  line-height: var(--lh-note);
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
}

/* ── Reserves: the box a block holds while its read is out ──────────────────────────────────── */

.reserve {
  display: block;
  border-radius: var(--radius-12px);
}

.reserveMonthDesk { height: 238px; }
.reserveMonthQuick { height: 120px; }
.reservePlanDesk { height: 298px; }
.reservePlanQuick { height: var(--h-talia-plan-cover); }
.reserveGoal { height: 58px; }
.reserveInvitation { height: 220px; }
.reserveLines { height: 150px; }

/* Three quick lines of 26, the last without its hairline — `.linesQuick`. */
.reserveLinesQuick { height: 78px; }

/* The session's detail AND its note field, which arrive together (`SessionBlock`). */
.reserveSession { height: var(--h-session-detail); }

/* Focus, last, so it outranks every resting declaration above. */
.pick:focus-visible,
.act:focus-visible,
.chat:focus-visible {
  outline: var(--border-focus) solid var(--accent);
  outline-offset: var(--space-1px);
}
